<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
    </section>

    <section class="dp-right">
      <div class="dish-statistics-container">
        <!-- 页面顶部 -->
        <div class="page-header">
          <div class="page-title">
            <h2>订餐统计大屏</h2>
            <span class="subtitle">数据概览与实时销售趋势</span>
          </div>
          <div class="header-actions">
            <!-- 修改为 daterange 模式 -->
            <el-date-picker v-model="dateRange" type="daterange" start-placeholder="开始日期" end-placeholder="结束日期"
              format="yyyy-MM-dd" value-format="yyyy-MM-dd" :clearable="false" class="custom-date-picker"
              style="width: 320px;">
            </el-date-picker>
            <el-button type="primary" class="modern-btn" @click="handleSearch" :loading="loading">
              <i class="el-icon-search"></i> 查询分析
            </el-button>
            <el-button class="modern-btn btn-outline" @click="handleReset">重置</el-button>
            <el-button type="success" class="modern-btn btn-export" @click="exportExcel" :loading="exportLoading">
              <i class="el-icon-download"></i> 区间导出
            </el-button>
          </div>
        </div>

        <!-- 数据加载中 -->
        <div v-if="loading" class="loading-wrapper">
          <div class="loader"></div>
          <p>正在生成多维分析数据...</p>
        </div>

        <div v-else class="dashboard-content">
          <!-- 核心指标卡片 -->
          <div class="kpi-cards" v-if="hasData">
            <div class="kpi-card blue">
              <i class="el-icon-s-order kpi-mini-icon"></i>
              <div class="kpi-info">
                <span class="kpi-label">总订餐量:</span>
                <span class="kpi-value-small">{{ totalOrders }}</span>
              </div>
            </div>
            <div class="kpi-card green">
              <i class="el-icon-dish kpi-mini-icon"></i>
              <div class="kpi-info">
                <span class="kpi-label">供应菜品:</span>
                <span class="kpi-value-small">{{ uniqueDishesCount }}</span>
              </div>
            </div>
            <div class="kpi-card purple">
              <i class="el-icon-box kpi-mini-icon"></i>
              <div class="kpi-info">
                <span class="kpi-label">总供应量:</span>
                <span class="kpi-value-small">{{ totalDishQuantity }}</span>
              </div>
            </div>
            <div class="kpi-card orange">
              <i class="el-icon-data-line kpi-mini-icon"></i>
              <div class="kpi-info">
                <span class="kpi-label">动销率:</span>
                <span class="kpi-value-small">{{ overallSalesRate }}%</span>
              </div>
            </div>
          </div>

          <div v-if="!hasData" class="empty-state">
            <svg class="empty-icon" viewBox="0 0 1024 1024" width="200" height="200">
              <path
                d="M512 85.333333c-235.648 0-426.666667 191.018667-426.666667 426.666667s191.018667 426.666667 426.666667 426.666667 426.666667-191.018667 426.666667-426.666667-191.018667-426.666667-426.666667-426.666667z m0 768c-188.501333 0-341.333333-152.832-341.333333-341.333333s152.832-341.333333 341.333333-341.333333 341.333333 152.832 341.333333 341.333333-152.832 341.333333-341.333333 341.333333z"
                fill="#E4E7ED"></path>
              <path
                d="M512 298.666667a42.666667 42.666667 0 0 0-42.666667 42.666667v170.666666a42.666667 42.666667 0 0 0 85.333334 0V341.333334a42.666667 42.666667 0 0 0-42.666667-42.666667zM512 640a42.666667 42.666667 0 1 0 0 85.333333 42.666667 42.666667 0 0 0 0-85.333333z"
                fill="#E4E7ED"></path>
            </svg>
            <p>{{ statisticsData ? '该日期暂无排餐汇总数据' : '请选择日期并查询' }}</p>
          </div>

          <!-- 主要内容区 -->
          <div v-if="hasData" class="main-dashboard flat-view">
            <!-- 左侧：菜品明细长列表 -->
            <div class="left-panel">
              <div class="panel-card glass-panel no-padding-bottom">
                <!-- 动态餐次 Tabs -->
                <el-tabs v-model="activeTab" class="custom-tabs" @tab-click="handleTabClick">
                  <el-tab-pane v-for="item in statisticsData.Items" :key="item.DinnerTimeId"
                    :label="item.DinnerTimeName" :name="item.DinnerTimeId.toString()">
                  </el-tab-pane>
                </el-tabs>

                <div class="batch-header-mini">
                  <div class="batch-meta">
                    <span class="meta-item"><i class="el-icon-sell"></i> 选定餐次总销量: <b>{{ totalOrders }}</b></span>
                  </div>
                </div>

                <div class="dish-grid">
                  <div class="dish-item" v-for="(dish, index) in pagedDishes" :key="dish.DishId">
                    <div class="dish-rank" :class="'rank-' + (dish.originalIndex + 1)">{{ dish.originalIndex + 1 }}
                    </div>
                    <div class="dish-info-wrapper">
                      <div class="dish-name-row">
                        <span class="d-name">{{ dish.DishName }}</span>
                        <span class="d-sales">{{ dish.OrderedCount }} 份</span>
                      </div>
                      <div class="dish-sub-info" v-if="dish.Position">
                        <i class="el-icon-location-outline"></i> {{ dish.Position }}
                      </div>
                      <div class="progress-container">
                        <div class="progress-track">
                          <div class="progress-fill" :class="getFillClass(dish)"
                            :style="{ width: getProgressWidth(dish) + '%' }"></div>
                        </div>
                        <div class="progress-stats">
                          <span class="stat-remaining">剩余: {{ dish.RemainingCount >= 0 ? dish.RemainingCount : '充足'
                          }}</span>
                          <span class="stat-total">总量: {{ dish.DailyTotal }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 前端分页控制 -->
                <div class="pagination-container" v-if="allDishes.length > pageSize">
                  <el-pagination @current-change="handlePageChange" :current-page.sync="currentPage"
                    :page-size="pageSize" layout="total, prev, pager, next" :total="allDishes.length" small />
                </div>
              </div>
            </div>

            <!-- 右侧：可视化图表 -->
            <div class="right-panel">
              <!-- 核心热销图表 -->
              <div class="panel-card chart-card full-height">
                <div class="card-title">
                  <h4>{{ activeTabLabel }} 菜品销售排行</h4>
                </div>
                <div ref="topDishesChart" class="echarts-box large-box"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import * as echarts from 'echarts';
import { getDishStatistics, exportDayStatExcel } from '../../api/modules/catering';
import DepartmentPane from '../../components/DepartmentPane'

export default {
  components: { DepartmentPane },
  name: 'DishStatistics',
  data() {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    return {
      dateRange: [todayStr, todayStr], // 改为数组支持范围
      statisticsData: null,
      loading: false,
      exportLoading: false,
      topDishesChart: null,
      DepartmentId: 0,
      activeTab: "0", // 默认选中第一个 Tab (通常是汇总)
      currentPage: 1,
      pageSize: 6 // 每页显示6个
    };
  },
  computed: {
    hasData() {
      return this.statisticsData && this.statisticsData.Items && this.statisticsData.Items.length > 0;
    },
    activeTabLabel() {
      if (!this.hasData) return "";
      const item = this.statisticsData.Items.find(it => it.DinnerTimeId.toString() === this.activeTab);
      return item ? item.DinnerTimeName : "";
    },
    allDishes() {
      if (!this.hasData) return [];
      const item = this.statisticsData.Items.find(it => it.DinnerTimeId.toString() === this.activeTab);
      if (!item) return [];
      // 直接取对应餐次的菜品项并记录原始索引用于排名
      const dishes = item.Dishes || [];
      return dishes.map((d, i) => ({ ...d, originalIndex: i }));
    },
    pagedDishes() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.allDishes.slice(start, end);
    },
    totalOrders() {
      if (!this.hasData) return 0;
      return this.allDishes.reduce((sum, dish) => sum + dish.OrderedCount, 0);
    },
    uniqueDishesCount() {
      return this.allDishes.length;
    },
    totalDishQuantity() {
      if (!this.hasData) return 0;
      return this.allDishes.reduce((sum, dish) => sum + dish.DailyTotal, 0);
    },
    overallSalesRate() {
      if (!this.hasData) return 0;
      let total = 0;
      let ordered = 0;
      this.allDishes.forEach(d => {
        total += d.DailyTotal;
        ordered += d.OrderedCount;
      });
      if (total === 0) return 0;
      return Math.round((ordered / total) * 100);
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    this.destroyCharts();
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    treeNodeClick(data, node, component) {
      if (data.value === 0 || data.value === '0' || data.label === '总平台') {
        this.$message.warning("请选择具体的学校/部门查看汇总数据");
        this.DepartmentId = 0;
        this.statisticsData = { OrderDate: "", Items: [] };
        this.destroyCharts();
        return;
      }
      this.DepartmentId = data.value;
      this.handleSearch();
    },

    destroyCharts() {
      if (this.topDishesChart) this.topDishesChart.dispose();
    },

    handleSearch() {
      if (!this.dateRange || this.dateRange.length < 2) {
        this.$message.warning("请选择日期范围");
        return;
      }
      this.loading = true;
      this.currentPage = 1; // 重新搜索时重置分页

      const para = {
        date: this.dateRange[0],
        endDate: this.dateRange[1],
        departmentId: this.DepartmentId
      };

      getDishStatistics(para)
        .then(response => {
          if (response.data.success && response.data.response && response.data.response.DailyStats) {
            // 大屏显示默认取“全周期汇总” (即第一个元素)
            this.statisticsData = response.data.response.DailyStats[0];
            // 如果原来的选中的 Tab 在新数据中不存在，则重置为汇总(0)
            if (this.statisticsData.Items.length > 0) {
              const tabExists = this.statisticsData.Items.some(it => it.DinnerTimeId.toString() === this.activeTab);
              if (!tabExists) {
                this.activeTab = this.statisticsData.Items[0].DinnerTimeId.toString();
              }
            }
          } else {
            this.statisticsData = { OrderDate: "", Items: [] };
          }
        })
        .catch(error => {
          console.error('获取统计数据失败:', error);
          this.statisticsData = { OrderDate: "", Items: [] };
        })
        .finally(() => {
          this.loading = false;
          this.$nextTick(() => {
            if (this.hasData) {
              this.renderCharts();
            }
          });
        });
    },

    handleTabClick() {
      this.currentPage = 1;
      this.$nextTick(() => {
        this.renderCharts();
      });
    },

    handleReset() {
      const d = new Date();
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const today = `${year}-${month}-${day}`;
      this.dateRange = [today, today];
      this.handleSearch();
    },

    getProgressWidth(dish) {
      if (!dish || dish.DailyTotal <= 0) return 100;
      const progress = (dish.OrderedCount / dish.DailyTotal) * 100;
      return Math.min(progress, 100);
    },

    getFillClass(dish) {
      if (!dish || dish.DailyTotal <= 0) return 'fill-normal';
      const rate = dish.OrderedCount / dish.DailyTotal;
      if (rate >= 0.9) return 'fill-danger'; // 快卖光了
      if (rate >= 0.7) return 'fill-warning';
      return 'fill-normal';
    },

    renderCharts() {
      this.destroyCharts();
      this.renderTopDishesChart();
    },

    renderTopDishesChart() {
      if (!this.$refs.topDishesChart) return;
      this.topDishesChart = echarts.init(this.$refs.topDishesChart);

      const sortedDishes = [...this.allDishes]
        .sort((a, b) => b.OrderedCount - a.OrderedCount)
        .slice(0, 10); // 取前10个

      if (sortedDishes.length === 0) return;

      // 反转一下，让销量最高的在最上面显示
      sortedDishes.reverse();

      const option = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: '3%', right: '15%', bottom: '10%', top: '5%', containLabel: true },
        xAxis: {
          type: 'value',
          show: true,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { type: 'dashed' } }
        },
        yAxis: {
          type: 'category',
          data: sortedDishes.map(d => d.DishName),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#5e6d82', fontWeight: 'bold' }
        },
        series: [
          {
            type: 'bar',
            data: sortedDishes.map(d => d.OrderedCount),
            barWidth: 16,
            itemStyle: {
              borderRadius: [0, 8, 8, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#f56c6c' },
                { offset: 1, color: '#ff9a9e' }
              ])
            },
            label: {
              show: true,
              position: 'right',
              color: '#303133',
              fontWeight: 'bold',
              formatter: '{c} 份'
            }
          }
        ]
      };
      this.topDishesChart.setOption(option);
    },

    handlePageChange(val) {
      this.currentPage = val;
    },



    handleResize() {
      if (this.topDishesChart) this.topDishesChart.resize();
    },

    async exportExcel() {
      if (!this.dateRange || this.dateRange.length < 2) return;
      try {
        let para = {
          date: this.dateRange[0],
          endDate: this.dateRange[1],
          departmentId: this.DepartmentId
        };
        var fileName = `订餐统计报表_${this.dateRange[0]}_${this.dateRange[1]}`;
        const response = await exportDayStatExcel(para);
        if (!response.data) {
          this.$message.error("导出数据为空");
          return;
        }
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('下载失败:', error);
        this.$message.error("导出报表失败，请稍后重试");
      }
    }
  }
};
</script>

<style scoped>
/* 全局布局与背景 */
.dp-layout {
  height: 100%;
  background-color: #f0f2f5;
}

.dish-statistics-container {
  height: 100%;
  box-sizing: border-box;
}

/* 顶部 Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: #fff;
  padding: 20px 24px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.page-title h2 {
  margin: 0 0 4px 0;
  font-size: 18px;
  color: #1f2f3d;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.subtitle {
  font-size: 13px;
  color: #909399;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

/* 日期选择器修正 */
.custom-date-picker>>>.el-input__inner {
  height: 40px;
  line-height: 40px;
  border-radius: 8px;
  border: 1px solid #dcdfe6;
}

/* 现代化按钮 */
.modern-btn {
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 500;
  transition: all 0.3s;
  height: 36px;
}

.btn-outline {
  border: 1px solid #dcdfe6;
  color: #606266;
  background: transparent;
}

.btn-outline:hover {
  border-color: var(--btn-primary, #2563eb);
  color: var(--btn-primary, #2563eb);
  background: #f0f7ff;
}

/* 加载动画 */
.loading-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.02);
}

.loader {
  border: 3px solid #f3f3f3;
  border-top: 3px solid var(--btn-primary, #2563eb);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* 核心 KPI 卡片 */
.kpi-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 8px;
}

.kpi-card {
  background: #fff;
  padding: 16px 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.kpi-mini-icon {
  font-size: 26px;
  margin-right: 16px;
}

.kpi-card.blue .kpi-mini-icon {
  color: var(--btn-primary, #2563eb);
}

.kpi-card.green .kpi-mini-icon {
  color: #67C23A;
}

.kpi-card.purple .kpi-mini-icon {
  color: #9c27b0;
}

.kpi-card.orange .kpi-mini-icon {
  color: #E6A23C;
}

.kpi-info {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.kpi-label {
  font-size: 15px;
  color: #606266;
  font-weight: 500;
}

.kpi-value-small {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
}

/* 仪表盘主体布局 */
.main-dashboard {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 16px;
}

.panel-card {
  background: #fff;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  margin-bottom: 12px;
}

.glass-panel {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
}

/* 批次列表头部 */
.batch-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px dashed #ebeef5;
}

.batch-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.badge {
  background: #fdf6ec;
  color: #e6a23c;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}

.batch-title h3 {
  margin: 0;
  font-size: 18px;
  color: #303133;
}

.batch-meta {
  color: #606266;
  font-size: 14px;
}

.batch-meta b {
  color: var(--btn-primary, #2563eb);
  font-size: 18px;
}

/* 菜品网格 - 现代列表 */
.dish-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.dish-item:hover {
  background: #fff;
  border-color: #c6e2ff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.1);
}

/* 分页居中 */
.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f0f2f5;
  padding-bottom: 10px;
}

.custom-tabs {
  margin-bottom: 15px;
}

.custom-tabs /deep/ .el-tabs__header {
  margin-bottom: 10px;
}

.custom-tabs /deep/ .el-tabs__item {
  font-size: 15px;
  font-weight: 500;
}

.no-padding-bottom {
  padding-bottom: 0 !important;
}

.batch-header-mini {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.dish-rank {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #e4e7ed;
  color: #909399;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  font-size: 13px;
  margin-right: 12px;
  flex-shrink: 0;
}

.dish-rank.rank-1 {
  background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
  color: #fff;
}

.dish-rank.rank-2 {
  background: linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%);
  color: #fff;
}

.dish-rank.rank-3 {
  background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%);
  color: #fff;
}

.dish-info-wrapper {
  flex: 1;
}

.dish-name-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.d-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.d-sales {
  font-size: 16px;
  font-weight: bold;
  color: #f56c6c;
}

.dish-sub-info {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 自定义渐变进度条 */
.progress-container {
  width: 100%;
}

.progress-track {
  height: 8px;
  background: #ebeef5;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 6px;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.fill-normal {
  background: linear-gradient(90deg, #67C23A, #85ce61);
}

.fill-warning {
  background: linear-gradient(90deg, #E6A23C, #ebb563);
}

.fill-danger {
  background: linear-gradient(90deg, #F56C6C, #f78989);
}

.progress-stats {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #909399;
}

/* 图表区 */
.card-title h4 {
  margin: 0;
  font-size: 16px;
  color: #303133;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.echarts-box {
  height: 250px;
  width: 100%;
  margin-top: 16px;
}

.echarts-box.large-box {
  height: 400px;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  flex: 1;
  background: #fff;
  border-radius: 16px;
}

.empty-state p {
  color: #909399;
  font-size: 16px;
  margin-top: 16px;
}

@media (max-width: 1400px) {
  .dish-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1100px) {
  .main-dashboard {
    grid-template-columns: 1fr;
  }

  .right-panel {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
}

@media (max-width: 768px) {
  .kpi-cards {
    grid-template-columns: 1fr;
  }

  .right-panel {
    grid-template-columns: 1fr;
  }

  .header-actions {
    flex-direction: column;
    width: 100%;
  }

  .header-actions .el-date-editor {
    width: 100%;
    margin-bottom: 10px;
  }

  .modern-btn {
    width: 100%;
    margin-bottom: 10px;
  }
}
</style>
